// Money in Dutch is written with a COMMA. A field that silently strips what it
// does not understand turns "12,50" into 1250 or 12 and nobody notices until a
// repair total is a hundred times wrong — and those totals decide repair or
// replace, and what goes to a landlord.
//
// So: never remove a character the user typed. Read what they wrote, and if it
// cannot be read as an amount, SAY SO.

// The dash is allowed through the gate ONLY so the Dutch shorthand "100,-" can
// reach the rule below that consumes it. A minus sign anywhere else still fails
// further down (a negative cost is not an amount here), so nothing is guessed.
const ONLY_MONEY_CHARS = /^[0-9.,\-\s\u00a0€]+$/;

/**
 * Parse a human-typed amount. Accepts a comma or a period as the decimal
 * separator, accepts thousands separators, accepts a euro sign and spaces.
 * Returns { value, error }: value is null for an empty field (allowed) and for
 * an unreadable one (with error set). Never guesses.
 */
export function parseAmount(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return { value: null, error: null };
  if (!ONLY_MONEY_CHARS.test(s)) {
    return { value: null, error: "Dit is geen bedrag. Gebruik cijfers, bijvoorbeeld 12,50." };
  }
  const t0 = s.replace(/[\s\u00a0€]/g, "");
  // "100,-" and "1.000,-" are Dutch for "and no cents". Consuming that ending
  // also TELLS us there is no decimal part, so any separator left in front of it
  // can only be a thousands group — without that, "1.000,-" was refused as
  // ambiguous even though the writer had already said the cents are zero.
  const noCents = /[.,]-$/.test(t0);
  const t = noCents ? t0.replace(/[.,]-$/, "") : t0;
  if (!/[0-9]/.test(t)) {
    return { value: null, error: "Dit is geen bedrag. Gebruik cijfers, bijvoorbeeld 12,50." };
  }

  const lastComma = t.lastIndexOf(",");
  const lastDot = t.lastIndexOf(".");
  const AMBIGUOUS = "Onduidelijk bedrag: gebruik een komma voor de centen, bijvoorbeeld 12,50.";
  let decimalSep = null;
  if (noCents) {
    decimalSep = null;             // the writer already said: no cents
  } else if (lastComma >= 0 && lastDot >= 0) {
    // Both present: the LAST one is the decimal separator, the other groups.
    decimalSep = lastComma > lastDot ? "," : ".";
  } else if (lastComma >= 0 || lastDot >= 0) {
    const sep = lastComma >= 0 ? "," : ".";
    const parts = t.split(sep);
    if (parts.length === 2 && parts[1].length <= 2) {
      decimalSep = sep;             // 12,50 — cents
    } else if (parts.length === 2 && parts[1].length === 3) {
      // "12,505" is a decimal comma to a Dutchman and a thousands group to an
      // American. Refusing is the only honest answer: guessing here is the
      // hundred-times-wrong bug this whole helper exists to prevent.
      return { value: null, error: AMBIGUOUS };
    } else {
      decimalSep = null;           // 1.234.567 — grouping only
    }
  }

  // Grouping must actually look like grouping, or we do not know what was meant.
  const beforeDecimal = decimalSep ? t.slice(0, t.lastIndexOf(decimalSep)) : t;
  if (/[.,]/.test(beforeDecimal)) {
    const groups = beforeDecimal.split(/[.,]/);
    const ok = groups[0].length >= 1 && groups[0].length <= 3 &&
      groups.slice(1).every((g) => g.length === 3);
    if (!ok) return { value: null, error: AMBIGUOUS };
  }

  let intPart = t;
  let decPart = "";
  if (decimalSep) {
    const at = t.lastIndexOf(decimalSep);
    intPart = t.slice(0, at);
    decPart = t.slice(at + 1);
  }
  intPart = intPart.replace(/[.,]/g, "");
  if (!/^[0-9]*$/.test(intPart) || !/^[0-9]*$/.test(decPart)) {
    return { value: null, error: "Dit is geen bedrag. Gebruik cijfers, bijvoorbeeld 12,50." };
  }
  const value = Number(`${intPart || "0"}.${decPart || "0"}`);
  if (!Number.isFinite(value)) {
    return { value: null, error: "Dit is geen bedrag. Gebruik cijfers, bijvoorbeeld 12,50." };
  }
  return { value: Math.round(value * 100) / 100, error: null };
}

/** Dutch form value for an existing amount: what is shown must be re-enterable
 *  and must parse back to the same number. No thousands separator, so a
 *  round-trip cannot depend on a group being read correctly. */
export function amountToInput(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toFixed(2).replace(".", ",").replace(/,00$/, "");
}

/** First error among a set of money fields, for the submit guard. */
export function firstAmountError(values) {
  for (const v of values) {
    const { error } = parseAmount(v);
    if (error) return error;
  }
  return null;
}
